import { useEffect, useRef } from 'react';

import { recordRequestRead } from '../services/requestReadMarker';

/**
 * An import you WATCHED land is not a change you have missed (#1575, Kiran's
 * issue 1, refined by the owner on 2026-10-06).
 *
 * The transcription worker writes the applied patch as a `request_versions` row
 * with `created_by_user_id: null`, and the unread rule reads a null author as
 * "somebody else" — so an audio or inline-text import turned the importer's own
 * row unread. The issue proposed attributing that version to the importer.
 *
 * ⚠️ THAT FIX WOULD HAVE MADE THE OWNER'S RULE IMPOSSIBLE, which is why it is
 * not the one taken. A version authored by me can never be unread FOR me, and
 * the rule needs exactly that case to survive:
 *
 *   started it and saw it finish  → read    (this hook writes the marker)
 *   left before it finished       → UNREAD  (no marker; the version stands)
 *   everybody else                → unread  (unchanged either way)
 *
 * So the version stays authored by nobody, and "I saw it" is recorded the only
 * way it can honestly be known: the screen was still mounted when the job
 * reached COMPLETED, which is the same moment it refreshes itself with the
 * imported content. Walk away first and no marker is written.
 *
 * ⚠️ KEYED ON THE JOB, NOT THE MOUNT. `RequestDetail` re-renders constantly, and
 * a person can run a second import without leaving: a new `completedJobKey`
 * fires again, the same one never fires twice.
 *
 * ⚠️ COMPLETED ONLY. A FAILED job applies no patch and writes no version, so
 * there is nothing to have seen and the marker must not move — moving it would
 * silently mark somebody ELSE's genuine change as read.
 *
 * Never for an `o-` draft, and never throwing: both for the same reasons as
 * `useRecordRequestRead`, whose contract this follows exactly.
 */
export const useRecordReadOnImportCompletion = (
  requestId: string | null | undefined,
  completedJobKey: string | null,
): void => {
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!requestId || requestId.startsWith('o-') || !completedJobKey) {
      return;
    }

    const key = `${requestId}:${completedJobKey}`;
    if (firedForRef.current === key) {
      return;
    }
    firedForRef.current = key;

    recordRequestRead(requestId, new Date().toISOString()).catch(() => {
      /* fire and forget — a marker that does not arrive costs one bold row */
    });
  }, [requestId, completedJobKey]);
};
